import { codeUnitString } from "../code-unit-string.js";
import { isPostgresStorable } from "../audit-text-bounds.js";
import { z } from "zod";
//#region src/mail/app-mail.ts
/**
* The wire contract of app mail (`2026-10-09-app-mail`, WS-1): the body a tenant app sends to
* core-api `POST /internal/mail/app`, and the body core-api answers with.
*
* Who uses it:
* - The app client of `@zion/sdk-node` (WS-2) builds a request and sends it to the fixed
*   mail route of the signer sidecar. The client holds no URL and no token (D-6).
* - The signer sidecar (WS-4) relays the body unchanged to {@link APP_MAIL_PATH} on core-api and
*   adds the pod's service bearer.
* - core-api (WS-5) binds {@link appMailSendRequestSchema} to the route body, checks the `krt`
*   claim for `mail.send` and `mail.external`, writes the attachments to object storage and writes
*   one outbox row. It answers 202 with {@link appMailSendResponseSchema}.
*
* The body carries no identity (D-3). The organization, the app and the principal come from the
* verified service bearer. So the body has no `orgId`, no `appId`, no `category` and no sender
* address: the From address stays on `zion.witness.ai` (D-22). An unknown key at any depth is a
* refusal, because every object here is strict.
*
* The app sends any content (D-16, D-17): HTML, text, a display name, a Reply-To, cc, bcc, custom
* headers and attachments. The schemas bound the size of each field and refuse a line break in
* every header field. They hold no `.transform()`, so they stay representable as JSON Schema
* (`createOpenApiDocument()`).
*
* The parse cost of a body is bounded before the schema reads an entry. The route admits a body of
* up to `APP_MAIL_LIMITS.requestMaxBytes`, and NestJS runs the validation pipe before the handler
* checks the `krt` claim. So each list and the header map count their entries first, and a string
* checks its length first. A body with a million cc entries or headers gets one issue, and the
* schema reads none of the entries.
*
* This module is browser-safe: it reads no Node global and imports no Node builtin.
*/
/**
* The core-api route of app mail, `POST /internal/mail/app`. The signer sidecar relays to this
* path. The `mail.send` unattended rule of `KERNEL_UNATTENDED_RULES` names the same operation, and
* core-api serves the route with a `mail.send` mark (WS-5, D-20, I-6, R-002).
*/
const APP_MAIL_PATH = "/internal/mail/app";
/**
* The size caps of {@link appMailSendRequestSchema}.
*
* - `addressMaxChars`: 254, the RFC 5321 path limit for one address.
* - `nameMaxChars`: 128, for a display name and `fromName`.
* - `subjectMaxChars`: 255.
* - `bodyMaxBytes`: 262144 (256 KiB) of UTF-8, for `html` and for `text`, each.
* - `recipientsMax`: 50 addresses over `to`, `cc` and `bcc` together. `replyTo` does not count.
* - `headersMax`: 20 custom headers.
* - `headerNameMaxChars`: 76, so a header line with a short value fits one 78-character line.
* - `headerValueMaxChars`: 998, the RFC 5322 line limit.
* - `attachmentsMax`: 10 attachments.
* - `attachmentsMaxBytes`: 10485760 (10 MiB) of decoded bytes over all attachments together. The
*   base64 form is about 13.4 MiB, so a mail stays under the 30 MB message limit of SendGrid.
* - `attachmentContentMaxChars`: 13981016, the base64 length of 10 MiB. A longer string is a
*   refusal before the schema reads its characters.
* - `filenameMaxChars`: 255.
* - `contentTypeMaxChars`: 255.
* - `contentIdMaxChars`: 128.
* - `requestMaxBytes`: 18874368 (18 MiB). The UTF-8 length of `JSON.stringify` of every request
*   that the schema accepts is at most this value. The worst case is about 17.3 MB: 13981056
*   bytes of base64, two bodies of 262144 control characters that JSON escapes to 6 bytes each,
*   and every other field at its cap. The signer relay and the core-api route set their body
*   limit to this value.
*/
const APP_MAIL_LIMITS = {
	addressMaxChars: 254,
	nameMaxChars: 128,
	subjectMaxChars: 255,
	bodyMaxBytes: 262144,
	recipientsMax: 50,
	headersMax: 20,
	headerNameMaxChars: 76,
	headerValueMaxChars: 998,
	attachmentsMax: 10,
	attachmentsMaxBytes: 10485760,
	attachmentContentMaxChars: 13981016,
	filenameMaxChars: 255,
	contentTypeMaxChars: 255,
	contentIdMaxChars: 128,
	requestMaxBytes: 18874368
};
/**
* The header names an app may not set, in lower case and in alphabetical order. The schema
* compares a name without regard to case. Four groups:
*
* - The headers that the platform or the mail transport writes: the address and subject headers,
*   the MIME structure, the date, the message id, the trace headers `received` and
*   `delivered-to`, and the signature header `dkim-signature`.
* - The trace and authentication headers that a server writes when it takes the mail in, and
*   that the next server reads: `authentication-results` (RFC 8601), `received-spf` (RFC 7208,
*   the SPF verdict) and the three ARC headers `arc-authentication-results`,
*   `arc-message-signature` and `arc-seal` (RFC 8617). An app that sets one forges the verdict
*   of a server on platform mail (R-009, R-043). D-16 trusts the content of an app, and never the
*   trace and authentication headers.
* - The control headers that a mail provider reads from the message as an instruction (R-043):
*   `x-smtpapi`, the SMTP API header of the SendGrid SMTP relay, and `x-msys-api`, the SMTP API
*   header of the SparkPost relay. An app that sets one can add a recipient that the
*   `mail.external` check and the recipient limit never see. The prefixes of the other providers
*   are in {@link APP_MAIL_RESERVED_HEADER_PREFIXES}.
* - The headers that SendGrid refuses in its `headers` map.
*
* `List-Unsubscribe`, `In-Reply-To` and `References` are allowed: the app owns its opt-out (D-25)
* and its threads.
*/
const APP_MAIL_RESERVED_HEADERS = [
	"arc-authentication-results",
	"arc-message-signature",
	"arc-seal",
	"authentication-results",
	"bcc",
	"cc",
	"content-disposition",
	"content-id",
	"content-transfer-encoding",
	"content-type",
	"date",
	"delivered-to",
	"dkim-signature",
	"from",
	"message-id",
	"mime-version",
	"received",
	"received-spf",
	"reply-to",
	"return-path",
	"sender",
	"subject",
	"to",
	"x-msys-api",
	"x-smtpapi"
];
/**
* The header name prefixes an app may not use, in lower case. The schema compares without regard
* to case. The first three come first in the order of their first release, and the provider
* prefixes of R-043 follow in alphabetical order.
*
* - `x-zion-`: the headers of the platform.
* - `x-sg-`: the headers of SendGrid.
* - `resent-`: the RFC 5322 resent block (`Resent-From`, `Resent-Sender`, `Resent-To` and the
*   rest). A server reads a resent block as the sender of record of a forwarded mail, so an app
*   that sets one forges a resent sender on platform mail (R-009).
* - The control headers of the mail providers that read an instruction from the message
*   (R-043). Each prefix ends with `-`, so `X-Session-Id` and `X-MCP-Tool` stay free:
*   - `x-mailgun-`: Mailgun, for example `X-Mailgun-Variables` and `X-Mailgun-Drop-Message`.
*   - `x-mailin-`: Brevo, for example `X-Mailin-Tag`.
*   - `x-mailjet-`: Mailjet, for example `X-Mailjet-TrackClick`.
*   - `x-mc-`: Mailchimp Transactional (Mandrill), for example `X-MC-BccAddress`.
*   - `x-mj-`: Mailjet, for example `X-MJ-Vars`.
*   - `x-ms-exchange-`: Microsoft Exchange, for example `X-MS-Exchange-Organization-SCL`, the
*     spam verdict of the organization.
*   - `x-pm-`: Postmark, for example `X-PM-Message-Stream`.
*   - `x-ses-`: Amazon SES, for example `X-SES-CONFIGURATION-SET`.
*/
const APP_MAIL_RESERVED_HEADER_PREFIXES = [
	"x-zion-",
	"x-sg-",
	"resent-",
	"x-mailgun-",
	"x-mailin-",
	"x-mailjet-",
	"x-mc-",
	"x-mj-",
	"x-ms-exchange-",
	"x-pm-",
	"x-ses-"
];
/** The two content dispositions of an attachment. `inline` needs a `contentId`. */
const APP_MAIL_DISPOSITIONS = ["attachment", "inline"];
const HEADER_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9-]*$/;
const CONTENT_TYPE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9!#$&^_.+-]{0,126}\/[A-Za-z0-9][A-Za-z0-9!#$&^_.+-]{0,126}$/;
const CONTENT_ID_PATTERN = /^[A-Za-z0-9._@-]+$/;
/**
* True when `value` holds no code point that splits or folds a mail header: no C0 control (CR and
* LF among them), no DEL, no NEL, no LINE SEPARATOR and no PARAGRAPH SEPARATOR. The rule is the
* rule of `isHeaderSafe` in `@zion/sdk-node`, which this package cannot import.
*/
function isHeaderSafe(value) {
	for (let i = 0; i < value.length; i += 1) {
		const code = value.charCodeAt(i);
		if (code <= 31 || code === 127 || code === 133 || code === 8232 || code === 8233) return false;
	}
	return true;
}
/**
* One header field: 1 to `maxChars` characters, header-safe, storable in PostgreSQL `jsonb`. The
* length check aborts, so the schema reads no character of a string past the cap.
*/
function headerText(maxChars) {
	return z.string().min(1).max(maxChars, { abort: true }).refine((value) => isHeaderSafe(value) && isPostgresStorable(value), { message: "header field holds a line break, a control character, a NUL or a lone surrogate" });
}
const UTF8 = new TextEncoder();
/**
* One body: 1 character to `bodyMaxBytes` UTF-8 bytes, storable in PostgreSQL `jsonb`. Each UTF-16
* code unit takes at least 1 UTF-8 byte, so the `.max()` on the length refuses a long string before
* the byte count encodes it.
*/
function bodyText() {
	return codeUnitString().min(1).max(APP_MAIL_LIMITS.bodyMaxBytes, { abort: true }).refine(isPostgresStorable, { message: "body holds a NUL or a lone surrogate" }).refine((value) => UTF8.encode(value).byteLength <= APP_MAIL_LIMITS.bodyMaxBytes, { message: `body is larger than ${APP_MAIL_LIMITS.bodyMaxBytes} bytes` });
}
/**
* `element` as a list of 1 to `max` entries, with the count checked first. A list longer than
* `max` gets one `too_big` issue at the list, and no entry runs through `element`. Any other value
* goes on to `z.array(element).min(1).max(max)`, which gives the type issue for a non-list.
*
* The count step is a `z.preprocess()` that returns its input unchanged. JSON Schema skips that
* step in both `io` modes, so the list keeps its `minItems`, `maxItems` and `items` there.
*/
function countedList(element, max) {
	return z.preprocess((value, ctx) => {
		if (Array.isArray(value) && value.length > max) ctx.addIssue({
			code: "too_big",
			origin: "array",
			maximum: max,
			inclusive: true,
			input: value
		});
		return value;
	}, z.array(element).min(1).max(max));
}
/** True when `code` is a UTF-16 code unit of the base64 alphabet: `A` to `Z`, `a` to `z`, `0` to `9`, `+` and `/`. */
function isBase64Code(code) {
	return code >= 65 && code <= 90 || code >= 97 && code <= 122 || code >= 48 && code <= 57 || code === 43 || code === 47;
}
/**
* True when `value` is padded base64 (RFC 4648 section 4, the `+` and `/` alphabet): a length that
* is a multiple of 4, at least 4 characters, alphabet characters only, and 0 to 2 `=` at the end.
* The check reads each character once, with no regular expression, because a value holds up to
* 13981016 characters.
*/
function isPaddedBase64(value) {
	if (value.length === 0 || value.length % 4 !== 0) return false;
	const padding = paddingOf(value);
	for (let i = 0; i < value.length - padding; i += 1) if (!isBase64Code(value.charCodeAt(i))) return false;
	return true;
}
/** The number of `=` characters at the end of `value`, 0 to 2. */
function paddingOf(value) {
	if (value.endsWith("==")) return 2;
	return value.endsWith("=") ? 1 : 0;
}
/** The number of bytes that a padded base64 string decodes to. The caller passes a value that {@link isPaddedBase64} admits. */
function decodedLength(base64) {
	return base64.length / 4 * 3 - paddingOf(base64);
}
/**
* One mail address with an optional display name.
*
* - `address`: at most 254 characters, then `z.email()`. The length check aborts, so an address
*   past the cap gives one `too_big` issue and the email pattern reads no character of it.
* - `name`: an optional display name, 1 to 128 characters, header-safe and storable.
*
* Strict: an unknown key is a refusal.
*/
const appMailAddressSchema = z.strictObject({
	address: z.string().max(APP_MAIL_LIMITS.addressMaxChars, { abort: true }).check(z.email()),
	name: headerText(APP_MAIL_LIMITS.nameMaxChars).optional()
});
/**
* One attachment, with its bytes inline as base64. core-api writes the bytes to object storage, and
* the outbox row holds a reference (D-23).
*
* - `filename`: 1 to 255 characters, header-safe and storable, with no `/` and no `\`, and not
*   `.` or `..`.
* - `contentType`: a MIME type `type/subtype` with no parameter, each part 1 to 127 characters of
*   the RFC 6838 restricted-name set, for example `application/pdf`. Case does not matter.
* - `content`: the bytes as padded base64 (RFC 4648 section 4), at least 1 byte, at most
*   13981016 characters.
* - `disposition`: `attachment` or `inline`.
* - `contentId`: 1 to 128 characters of letters, digits, `.`, `_`, `@` and `-`. It is required
*   when `disposition` is `inline`, so the HTML can name it as `cid:<contentId>`, and refused when
*   `disposition` is `attachment`.
*
* Strict: an unknown key is a refusal.
*/
const appMailAttachmentSchema = z.strictObject({
	filename: headerText(APP_MAIL_LIMITS.filenameMaxChars).refine((value) => !value.includes("/") && !value.includes("\\") && value !== "." && value !== "..", { message: "filename holds a path separator or names a directory" }),
	contentType: z.string().max(APP_MAIL_LIMITS.contentTypeMaxChars, { abort: true }).regex(CONTENT_TYPE_PATTERN),
	content: z.string().max(APP_MAIL_LIMITS.attachmentContentMaxChars, { abort: true }).refine(isPaddedBase64, { message: "content is not padded base64 of at least 1 byte" }),
	disposition: z.enum(APP_MAIL_DISPOSITIONS),
	contentId: z.string().max(APP_MAIL_LIMITS.contentIdMaxChars, { abort: true }).regex(CONTENT_ID_PATTERN).optional()
}).superRefine((attachment, ctx) => {
	if (attachment.disposition === "inline" && attachment.contentId === void 0) ctx.addIssue({
		code: "custom",
		path: ["contentId"],
		message: "an inline attachment needs a contentId"
	});
	if (attachment.disposition === "attachment" && attachment.contentId !== void 0) ctx.addIssue({
		code: "custom",
		path: ["contentId"],
		message: "contentId is for an inline attachment only"
	});
});
/**
* True when `name` is a custom header name an app may set: it matches the name pattern and is
* neither a reserved name nor under a reserved prefix, without regard to case.
*/
function isAllowedHeaderName(name) {
	if (!HEADER_NAME_PATTERN.test(name)) return false;
	const lower = name.toLowerCase();
	if (APP_MAIL_RESERVED_HEADERS.includes(lower)) return false;
	return !APP_MAIL_RESERVED_HEADER_PREFIXES.some((prefix) => lower.startsWith(prefix));
}
/** True when `value` is an object and not a list: the values the header count reads. */
function isPlainMap(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
/**
* The custom headers of a mail: a map of header name to value.
*
* - A name is 1 to 76 characters: a letter, then letters, digits and `-`. It is not one of
*   {@link APP_MAIL_RESERVED_HEADERS} and does not start with one of
*   {@link APP_MAIL_RESERVED_HEADER_PREFIXES}, without regard to case.
* - A value is 1 to 998 characters, header-safe and storable.
* - The map holds at most 20 names, and no two names differ only in case.
*
* The count comes first. A map of more than 20 names gets one issue, `at most 20 headers` at the
* map, and no name or value runs through its check. Any other value goes on to the record schema,
* which gives the type issue for a value that is not a plain object.
*/
const appMailHeadersSchema = z.preprocess((value, ctx) => {
	if (isPlainMap(value) && Object.keys(value).length > APP_MAIL_LIMITS.headersMax) ctx.addIssue({
		code: "custom",
		path: [],
		input: value,
		message: `at most ${APP_MAIL_LIMITS.headersMax} headers`
	});
	return value;
}, z.record(z.string().min(1).max(APP_MAIL_LIMITS.headerNameMaxChars, { abort: true }).refine(isAllowedHeaderName, { message: "header name is malformed or reserved" }), headerText(APP_MAIL_LIMITS.headerValueMaxChars)).superRefine((headers, ctx) => {
	const seen = /* @__PURE__ */ new Set();
	for (const name of Object.keys(headers)) {
		const lower = name.toLowerCase();
		if (seen.has(lower)) {
			ctx.addIssue({
				code: "custom",
				path: [name],
				message: "two header names differ only in case"
			});
			return;
		}
		seen.add(lower);
	}
}));
/**
* The body of `POST /internal/mail/app`: one mail that a tenant app asks the platform to send.
*
* Fields:
* - `to`: one recipient ({@link appMailAddressSchema}). One request writes one outbox row.
* - `cc`, `bcc`: optional lists of 1 to 49 recipients each.
* - `replyTo`: an optional Reply-To address.
* - `fromName`: an optional display name of the sender, 1 to 128 characters, header-safe. The
*   sender address is the platform's (D-22).
* - `subject`: 1 to 255 characters, header-safe.
* - `html`: the body, 1 character to 262144 UTF-8 bytes, storable.
* - `text`: an optional plain-text body under the same cap.
* - `headers`: optional custom headers ({@link appMailHeadersSchema}).
* - `attachments`: an optional list of 1 to 10 attachments ({@link appMailAttachmentSchema}),
*   at most 10485760 decoded bytes together.
*
* Rules over the whole body:
* - `to`, `cc` and `bcc` hold at most 50 addresses together.
* - No address appears twice over `to`, `cc` and `bcc`, without regard to case. SendGrid refuses
*   a bcc address that is also a to address.
* - No two inline attachments share a `contentId`.
*
* Each refusal is a zod issue with the path of the field. core-api answers it with 400. The
* schema reads no clock and no environment. Pure.
*/
const appMailSendRequestSchema = z.strictObject({
	to: appMailAddressSchema,
	cc: countedList(appMailAddressSchema, APP_MAIL_LIMITS.recipientsMax - 1).optional(),
	bcc: countedList(appMailAddressSchema, APP_MAIL_LIMITS.recipientsMax - 1).optional(),
	replyTo: appMailAddressSchema.optional(),
	fromName: headerText(APP_MAIL_LIMITS.nameMaxChars).optional(),
	subject: headerText(APP_MAIL_LIMITS.subjectMaxChars),
	html: bodyText(),
	text: bodyText().optional(),
	headers: appMailHeadersSchema.optional(),
	attachments: countedList(appMailAttachmentSchema, APP_MAIL_LIMITS.attachmentsMax).optional()
}).superRefine((request, ctx) => {
	const addresses = appMailRecipients(request);
	if (addresses.length > APP_MAIL_LIMITS.recipientsMax) ctx.addIssue({
		code: "custom",
		path: [],
		message: `at most ${APP_MAIL_LIMITS.recipientsMax} recipients over to, cc and bcc`
	});
	const seen = /* @__PURE__ */ new Set();
	for (const address of addresses) {
		const lower = address.toLowerCase();
		if (seen.has(lower)) {
			ctx.addIssue({
				code: "custom",
				path: [],
				message: "an address appears twice over to, cc and bcc"
			});
			break;
		}
		seen.add(lower);
	}
	const attachments = request.attachments ?? [];
	if (attachments.reduce((sum, attachment) => sum + appMailAttachmentBytes(attachment), 0) > APP_MAIL_LIMITS.attachmentsMaxBytes) ctx.addIssue({
		code: "custom",
		path: ["attachments"],
		message: `attachments are larger than ${APP_MAIL_LIMITS.attachmentsMaxBytes} bytes together`
	});
	const contentIds = /* @__PURE__ */ new Set();
	for (const attachment of attachments) {
		if (attachment.contentId === void 0) continue;
		if (contentIds.has(attachment.contentId)) {
			ctx.addIssue({
				code: "custom",
				path: ["attachments"],
				message: "two inline attachments share a contentId"
			});
			break;
		}
		contentIds.add(attachment.contentId);
	}
});
/**
* The 202 body of `POST /internal/mail/app`: the id of the outbox row core-api wrote, a UUID. The
* row is `pending`; the outbox worker sends it later, and a send limit or a provider refusal can
* still end it. Strict: an unknown key is a refusal.
*/
const appMailSendResponseSchema = z.strictObject({ id: z.uuid() });
/**
* Every recipient address of `request`, in the order `to`, then `cc`, then `bcc`, as the request
* spells it. `replyTo` is not a recipient. core-api checks each address for the `mail.external`
* right and counts each against the per-recipient limit (D-23). Pure.
*/
function appMailRecipients(request) {
	return [
		request.to,
		...request.cc ?? [],
		...request.bcc ?? []
	].map((recipient) => recipient.address);
}
/**
* The number of bytes that the `content` of `attachment` decodes to. The caller passes an
* attachment that passed {@link appMailAttachmentSchema}; for any other value the answer has no
* meaning. Pure.
*/
function appMailAttachmentBytes(attachment) {
	return decodedLength(attachment.content);
}
//#endregion
export { APP_MAIL_DISPOSITIONS, APP_MAIL_LIMITS, APP_MAIL_PATH, APP_MAIL_RESERVED_HEADERS, APP_MAIL_RESERVED_HEADER_PREFIXES, appMailAddressSchema, appMailAttachmentBytes, appMailAttachmentSchema, appMailHeadersSchema, appMailRecipients, appMailSendRequestSchema, appMailSendResponseSchema };

//# sourceMappingURL=app-mail.js.map